"use client";

import { useState, useEffect, useRef, useId } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { RiFacebookFill, RiMailLine, RiTwitterXFill, RiWhatsappLine, RiTelegramLine, RiShare2Line } from "@remixicon/react";
import { CheckIcon, CopyIcon } from "lucide-react";
import { motion } from "framer-motion";
import { useTranslation } from "next-i18next";
export default function GlobalShare() {
  const {
    t
  } = useTranslation();
  const id = useId();
  const [copied, setCopied] = useState<boolean>(false);
  const [link, setLink] = useState<string>("");
  const [open, setOpen] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (typeof window !== "undefined") {
      setLink(window.location.href);
    }
  }, []);
  const handleCopy = () => {
    if (inputRef.current) {
      navigator.clipboard.writeText(inputRef.current.value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };
  const handleShareTwitter = () => {
    window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(link)}`, "_blank");
  };
  const handleShareFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}`, "_blank");
  };
  const handleShareEmail = () => {
    window.open(`mailto:?subject=Check%20this%20out&body=${encodeURIComponent(link)}`);
  };
  const handleShareWhatsApp = () => {
    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(link)}`, "_blank");
  };
  const handleShareTelegram = () => {
    window.open(`https://t.me/share/url?url=${encodeURIComponent(link)}&text=Check%20this%20out!`, "_blank");
  };

  // Framer Motion variants
  const popoverVariants = {
    hidden: {
      opacity: 0,
      scale: 0.95
    },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.2
      }
    }
  };
  return <div className="flex flex-col gap-4 w-fit" data-sentry-component="GlobalShare" data-sentry-source-file="global-share.tsx">
      <Popover open={open} onOpenChange={setOpen} data-sentry-element="Popover" data-sentry-source-file="global-share.tsx">
        <div onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
          <PopoverTrigger asChild className="border-neutral-500 dark:border-neutral-800 rounded-3xl shadow-md" data-sentry-element="PopoverTrigger" data-sentry-source-file="global-share.tsx">
            <Button variant="outline" data-sentry-element="Button" data-sentry-source-file="global-share.tsx">
              <RiShare2Line size={18} aria-hidden="true" className="text-aloeGold" data-sentry-element="RiShare2Line" data-sentry-source-file="global-share.tsx" /> {t('common:text-share')}
            </Button>
          </PopoverTrigger>
        </div>
        <PopoverContent onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)} className="w-72 p-4" data-sentry-element="PopoverContent" data-sentry-source-file="global-share.tsx">
          <motion.div initial="hidden" animate="visible" exit="hidden" variants={popoverVariants} data-sentry-element="unknown" data-sentry-source-file="global-share.tsx">
            <div className="flex flex-col gap-3 text-center">
              <div className="flex flex-wrap justify-center gap-2">
                <motion.div whileHover={{
                scale: 1.1
              }} whileTap={{
                scale: 0.95
              }} data-sentry-element="unknown" data-sentry-source-file="global-share.tsx">
                  <Button size="icon" variant="outline" aria-label="Share on Twitter" onClick={handleShareTwitter} data-sentry-element="Button" data-sentry-source-file="global-share.tsx">
                    <RiTwitterXFill size={18} aria-hidden="true" data-sentry-element="RiTwitterXFill" data-sentry-source-file="global-share.tsx" />
                  </Button>
                </motion.div>
                <motion.div whileHover={{
                scale: 1.1
              }} whileTap={{
                scale: 0.95
              }} data-sentry-element="unknown" data-sentry-source-file="global-share.tsx">
                  <Button size="icon" variant="outline" aria-label="Share on Facebook" onClick={handleShareFacebook} data-sentry-element="Button" data-sentry-source-file="global-share.tsx">
                    <RiFacebookFill size={18} aria-hidden="true" className="text-blue-500" data-sentry-element="RiFacebookFill" data-sentry-source-file="global-share.tsx" />
                  </Button>
                </motion.div>
                <motion.div whileHover={{
                scale: 1.1
              }} whileTap={{
                scale: 0.95
              }} data-sentry-element="unknown" data-sentry-source-file="global-share.tsx">
                  <Button size="icon" variant="outline" aria-label="Share via Email" onClick={handleShareEmail} data-sentry-element="Button" data-sentry-source-file="global-share.tsx">
                    <RiMailLine size={18} aria-hidden="true" className="text-red-500" data-sentry-element="RiMailLine" data-sentry-source-file="global-share.tsx" />
                  </Button>
                </motion.div>
                <motion.div whileHover={{
                scale: 1.1
              }} whileTap={{
                scale: 0.95
              }} data-sentry-element="unknown" data-sentry-source-file="global-share.tsx">
                  <Button size="icon" variant="outline" aria-label="Share on WhatsApp" onClick={handleShareWhatsApp} data-sentry-element="Button" data-sentry-source-file="global-share.tsx">
                    <RiWhatsappLine size={18} aria-hidden="true" className="text-green-500" data-sentry-element="RiWhatsappLine" data-sentry-source-file="global-share.tsx" />
                  </Button>
                </motion.div>
                <motion.div whileHover={{
                scale: 1.1
              }} whileTap={{
                scale: 0.95
              }} data-sentry-element="unknown" data-sentry-source-file="global-share.tsx">
                  <Button size="icon" variant="outline" aria-label="Share on Telegram" onClick={handleShareTelegram} data-sentry-element="Button" data-sentry-source-file="global-share.tsx">
                    <RiTelegramLine size={18} aria-hidden="true" className="text-purple-500" data-sentry-element="RiTelegramLine" data-sentry-source-file="global-share.tsx" />
                  </Button>
                </motion.div>
              </div>
              <div className="space-y-2">
                <div className="relative">
                  <Input ref={inputRef} id={id} className="pe-9" type="text" value={link} aria-label="Share link" readOnly data-sentry-element="Input" data-sentry-source-file="global-share.tsx" />
                  <TooltipProvider delayDuration={0} data-sentry-element="TooltipProvider" data-sentry-source-file="global-share.tsx">
                    <Tooltip data-sentry-element="Tooltip" data-sentry-source-file="global-share.tsx">
                      <TooltipTrigger asChild data-sentry-element="TooltipTrigger" data-sentry-source-file="global-share.tsx">
                        <button onClick={handleCopy} className="text-muted-foreground/80 hover:text-foreground focus-visible:border-ring focus-visible:ring-ring/50 absolute inset-y-0 end-0 flex h-full w-9 items-center justify-center rounded-e-md transition-[color,box-shadow] outline-none focus:z-10 focus-visible:ring-[3px] disabled:pointer-events-none disabled:cursor-not-allowed" aria-label={copied ? "Copied" : "Copy to clipboard"} disabled={copied}>
                          <div className={cn("transition-all", copied ? "scale-100 opacity-100" : "scale-0 opacity-0")}> 
                            <CheckIcon className="stroke-emerald-500" size={16} aria-hidden="true" data-sentry-element="CheckIcon" data-sentry-source-file="global-share.tsx" />
                          </div>
                          <div className={cn("absolute transition-all", copied ? "scale-0 opacity-0" : "scale-100 opacity-100")}>
                            <CopyIcon size={16} aria-hidden="true" data-sentry-element="CopyIcon" data-sentry-source-file="global-share.tsx" />
                          </div>
                        </button>
                      </TooltipTrigger>
                      <TooltipContent className="px-2 py-1 text-xs" data-sentry-element="TooltipContent" data-sentry-source-file="global-share.tsx">Copy</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
            </div>
          </motion.div>
        </PopoverContent>
      </Popover>
    </div>;
}